import React, { useState, useEffect, useMemo } from "react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useNavigate } from "react-router-dom";
import { useWhatsAppChat } from "../../context/WhatsAppChatContext";
import { useCart } from "../../context/CartContext";
import { useModalCartContext } from "../../context/ModalCartContext";

const FloatingButtons: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();
  const { toggleFabChat, isOpen } = useWhatsAppChat();
  const { cartState } = useCart();
  const { openModalCart } = useModalCartContext();

  const cartCount = useMemo(() => {
    return cartState.cartArray.reduce((n, item) => {
      const qty =
        Number(item.quantity) ||
        Number((item as { quantityPurchase?: number }).quantityPurchase) ||
        0;
      return n + Math.max(0, qty);
    }, 0);
  }, [cartState.cartArray]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      {showBackToTop && !isOpen && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-[140px] right-4 z-[51] hidden bg-pink text-white hover:bg-pink/80 md:flex p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:scale-110 items-center justify-center"
          aria-label="Retour en haut"
        >
          <Icon.CaretUp size={24} />
        </button>
      )}

      {/* Mobile: milieu droite — évite les boutons fixes « Poser une question » / « Achetez-le maintenant » */}
      <div className="fixed right-4 z-[51] flex flex-col items-center gap-3 top-1/2 -translate-y-1/2 md:top-auto md:translate-y-0 md:bottom-4">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center justify-center rounded-full bg-pink p-3 text-white shadow-lg transition-all duration-300 ease-in-out hover:scale-110 hover:bg-pink/90 md:hidden"
          aria-label="Accueil"
        >
          <Icon.House size={24} weight="bold" />
        </button>
        <button
          type="button"
          onClick={openModalCart}
          title={
            cartCount <= 1
              ? `Panier — ${cartCount} article`
              : `Panier — ${cartCount} articles`
          }
          className={`relative flex items-center justify-center rounded-full bg-pink p-3 text-white shadow-lg transition-all duration-300 ease-in-out hover:scale-110 hover:bg-pink/90 ${
            cartCount > 0 ? "ring-2 ring-white/60 ring-offset-2 ring-offset-transparent" : ""
          }`}
          aria-label={
            cartCount <= 1
              ? `Ouvrir le panier, ${cartCount} article`
              : `Ouvrir le panier, ${cartCount} articles`
          }
        >
          <Icon.ShoppingCart size={24} weight="bold" />
          <span
            className={`absolute -right-1 -top-1 flex min-h-[22px] min-w-[22px] items-center justify-center rounded-full px-1 text-[11px] font-bold leading-none shadow-sm tabular-nums ${
              cartCount > 0
                ? "bg-white text-pink"
                : "border border-white/70 bg-pink text-white"
            }`}
            aria-hidden
          >
            {cartCount > 99 ? "99+" : cartCount}
          </span>
        </button>

        <button
          type="button"
          onClick={toggleFabChat}
          style={{
            backgroundColor: isOpen ? "#128C7E" : "#25D366",
            color: "white",
            padding: "12px",
            borderRadius: "50%",
            border: "none",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            transition: "all 0.3s ease-in-out",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.backgroundColor = "#128C7E";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.backgroundColor = isOpen ? "#128C7E" : "#25D366";
          }}
          aria-expanded={isOpen}
          aria-label={
            isOpen ? "Fermer la fenêtre WhatsApp" : "Ouvrir le chat WhatsApp"
          }
        >
          {isOpen ? (
            <Icon.X size={24} weight="bold" />
          ) : (
            <Icon.ChatCircle size={24} />
          )}
        </button>
      </div>
    </>
  );
};

export default FloatingButtons;
